/**
 * The four content cards of the work-report detail page (D2f):
 * Beschreibung, Rapportdetails, Aufwand & Verlauf, Verknüpfungen.
 *
 * They share one shell (`DetailCard`) so border, radius, shadow,
 * padding and heading rhythm cannot drift apart — the reference design
 * shows a single card language across the whole page.
 *
 * All four are presentational: data in, markup out, no fetching and no
 * state. The page owns loading and errors.
 */
import React from "react";
import {
  Briefcase,
  Calendar,
  CalendarPlus,
  Clock,
  History,
  PenLine,
  Tag,
  Timer,
  User,
} from "lucide-react";

import { useCategoryDisplay } from "../activityCategories/ActivityCategoryChip";

import { formatDateTime } from "../../../utils/datetime";
import EntityLinkRows from "../entityLinks/EntityLinkRows";
import EntityLinkGroups from "../entityLinks/EntityLinkGroups";
import {
  assigneeText,
  categoryLabel,
  creatorText,
  formatDurationLong,
  reportDateText,
  reportLinks,
  spokenDurationLong,
  timeRangeText,
} from "./workReportDetailHelpers";

const CARD =
  "rounded-[12px] border border-border bg-card p-5 shadow-sm " +
  "dark:border-[#1e2130] dark:bg-[#13161f]";
const CARD_TITLE =
  "m-0 mb-4 text-[14px] font-semibold text-foreground dark:text-[#e2e4ed]";

/** Shared card shell. `headingId` is what the section's
 * `aria-labelledby` points at, so every card is a landmark with a real
 * accessible name. */
export function DetailCard({ headingId, title, children, className = "" }) {
  return (
    <section aria-labelledby={headingId} className={`${CARD} ${className}`}>
      <h2 id={headingId} className={CARD_TITLE}>
        {title}
      </h2>
      {children}
    </section>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Beschreibung
// ──────────────────────────────────────────────────────────────────────

/**
 * The full description — never clamped, never truncated.
 *
 * `whitespace-pre-line` keeps the author's paragraph breaks;
 * `break-words` stops an unbroken URL or a 60-character German compound
 * from pushing the card past the viewport. `max-w-prose` caps the
 * measure so long text stays readable on a wide screen.
 */
export function WorkReportDescriptionCard({ report }) {
  const text = (report?.description || "").trim();
  return (
    <DetailCard headingId="report-description-heading" title="Beschreibung">
      {text ? (
        <p className="m-0 max-w-prose whitespace-pre-line break-words text-[13px] leading-relaxed text-muted-foreground dark:text-[#94a3b8]">
          {text}
        </p>
      ) : (
        <p className="m-0 text-[13px] italic text-muted-foreground dark:text-[#4a5070]">
          Für diesen Rapport wurde keine Beschreibung erfasst.
        </p>
      )}
    </DetailCard>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Rapportdetails
// ──────────────────────────────────────────────────────────────────────

/** One labelled fact: icon + term on the left, value on the right.
 * Rendered as `<dt>`/`<dd>` so assistive tech reads it as the pair it
 * is; the icon is decorative and never the only carrier of meaning. */
function FactRow({ icon: Icon, label, value, valueLabel }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-border/60 py-2.5 last:border-b-0 dark:border-[#1e2130]/70">
      <dt className="flex min-w-0 shrink-0 items-center gap-2 text-[12px] text-muted-foreground dark:text-[#4a5070]">
        <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        {label}
      </dt>
      <dd
        className="m-0 min-w-0 break-words text-right text-[13px] font-medium text-foreground dark:text-[#c5cbe0]"
        aria-label={valueLabel}
      >
        {value}
      </dd>
    </div>
  );
}

/**
 * Every fact of the report as its own row, matching the reference
 * design's list. Only real data: no UUIDs, and `Zuletzt geändert` is
 * omitted entirely for a report that was never edited rather than
 * echoing the creation timestamp.
 */
export function WorkReportFactsCard({ report }) {
  const minutes = report?.duration_minutes;
  // RESP2d: the AREA's name comes from the organisation's catalog, so a
  // rename reaches this card without touching the report row. Named
  // apart from `categoryLabel` above, which is the report TYPE — the two
  // rows sit next to each other and sharing a name would be a trap.
  const { label: activityAreaLabel } = useCategoryDisplay(
    report?.activity_category
  );
  return (
    <DetailCard headingId="report-facts-heading" title="Rapportdetails">
      <dl className="m-0">
        <FactRow
          icon={Calendar}
          label="Datum"
          value={reportDateText(report?.work_date)}
        />
        <FactRow icon={Clock} label="Zeit" value={timeRangeText(report)} />
        <FactRow
          icon={Timer}
          label="Dauer"
          value={formatDurationLong(minutes)}
          valueLabel={spokenDurationLong(minutes)}
        />
        <FactRow
          icon={Tag}
          label="Kategorie"
          value={categoryLabel(report) || "—"}
        />
        {/* CAT1 on the report workspace: the AREA OF WORK, deliberately
            its own row next to the type — a cleaning report can be
            documentation work, and collapsing the two is what makes the
            reports useless. NULL reads as "Nicht zugewiesen", an
            unresolvable key as "Bereich nicht verfügbar", never a code. */}
        <FactRow
          icon={Briefcase}
          label="Tätigkeitsbereich"
          value={activityAreaLabel}
        />
        <FactRow icon={User} label="Mitarbeiter" value={assigneeText(report)} />
        <FactRow icon={PenLine} label="Erfasst von" value={creatorText(report)} />
        <FactRow
          icon={CalendarPlus}
          label="Erstellt am"
          value={report?.created_at ? formatDateTime(report.created_at) : "—"}
        />
        {report?.updated_at ? (
          <FactRow
            icon={History}
            label="Zuletzt geändert"
            value={formatDateTime(report.updated_at)}
          />
        ) : null}
      </dl>
    </DetailCard>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Aufwand & Verlauf
// ──────────────────────────────────────────────────────────────────────

function EffortStat({ icon: Icon, label, value, valueLabel }) {
  return (
    <div className="flex min-w-0 flex-1 items-center gap-2.5">
      <Icon
        className="h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400"
        aria-hidden="true"
      />
      <div className="min-w-0">
        <p className="m-0 text-[11px] text-muted-foreground dark:text-[#4a5070]">
          {label}
        </p>
        <p
          className="m-0 truncate text-[13px] font-semibold text-foreground dark:text-[#c5cbe0]"
          title={typeof value === "string" ? value : undefined}
          aria-label={valueLabel}
        >
          {value}
        </p>
      </div>
    </div>
  );
}

/**
 * The compact three-value strip: Dauer, Kategorie, Datum.
 *
 * The reference design shows a fourth value, a "Status: Dokumentiert"
 * chip. WorkReport has NO status column — the model comments call that
 * a locked MVP decision — so the chip would have to be hard-coded, and
 * a status that is the same on every report is decoration pretending to
 * be data. Dropped; the remaining three fill the row.
 *
 * Divided by borders on sm and up; on a narrow screen the values stack,
 * because three columns at 375 px would truncate all of them.
 */
export function WorkReportEffortCard({ report }) {
  const minutes = report?.duration_minutes;
  return (
    <DetailCard headingId="report-effort-heading" title="Aufwand &amp; Verlauf">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-0">
        <EffortStat
          icon={Clock}
          label="Dauer"
          value={formatDurationLong(minutes)}
          valueLabel={`Dauer: ${spokenDurationLong(minutes)}`}
        />
        <div className="hidden h-9 w-px shrink-0 bg-border dark:bg-[#1e2130] sm:block sm:mx-4" />
        <EffortStat
          icon={Tag}
          label="Kategorie"
          value={categoryLabel(report) || "—"}
        />
        <div className="hidden h-9 w-px shrink-0 bg-border dark:bg-[#1e2130] sm:block sm:mx-4" />
        <EffortStat
          icon={Calendar}
          label="Datum"
          value={reportDateText(report?.work_date)}
        />
      </div>
    </DetailCard>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Verknüpfungen
// ──────────────────────────────────────────────────────────────────────

/**
 * All eight possible links, always all eight rows.
 *
 * Showing the empty ones as "Nicht verknüpft" is the point: the card
 * answers "what is this report attached to" completely, and a missing
 * row would read as a rendering bug rather than as an absent link.
 *
 * Only foreign keys set on the report itself appear here. Nothing is
 * inferred — a report attached to a tenancy does not sprout that
 * tenancy's tenant or unit, and a room never contributes its own unit
 * or property as a link the person did not choose.
 *
 * D4c1: the rows themselves moved to ``entityLinks/EntityLinkRows`` so
 * the task detail page renders them identically; only this card's
 * chrome stays here.
 */
export function WorkReportLinksCard({ report }) {
  return (
    <DetailCard headingId="report-links-heading" title="Verknüpfungen">
      {/* The task stays its OWN row: it is an owner relation, not one
          of the seven entity links, and a report belongs to at most one
          task. ME3a groups only the entity links below it. */}
      <EntityLinkRows entries={reportLinks(report).filter((e) => e.key === "task")} />
      <EntityLinkGroups row={report} />
    </DetailCard>
  );
}
